import { useState } from "react";
import { Empty, Spin } from "antd";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import medicalReviewApi, {
  MedicalReviewApiError,
} from "@/services/medicalReviewApi";
import { useAppNotification } from "@/hooks/useAppNotification";
import MonitoringPlanView from "./MonitoringPlan";
import MonitoringPlanHistory from "./MonitoringPlanHistory";
import { useMonitoringPlanDownload } from "./useMonitoringPlanDownload";
import type { MonitoringPlanUpload } from "./medicalReview.types";

export default function MonitoringPlanTab({ trialId }: { trialId: string }) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const queryClient = useQueryClient();
  const { openSuccessNotification, openErrorNotification, contextHolder } =
    useAppNotification();
  const {
    download,
    downloading,
    contextHolder: downloadContextHolder,
  } = useMonitoringPlanDownload(trialId);

  const planQuery = useQuery({
    queryKey: ["medical-review-plan", trialId],
    queryFn: () => medicalReviewApi.plan(trialId),
  });

  const uploadMutation = useMutation({
    mutationFn: (draft: MonitoringPlanUpload) =>
      medicalReviewApi.uploadPlan(trialId, draft),
    onSuccess: (result) => {
      queryClient.invalidateQueries({
        queryKey: ["medical-review-plan", trialId],
      });
      queryClient.invalidateQueries({
        queryKey: ["medical-review-plan-history", trialId],
      });
      openSuccessNotification({
        title: `Monitoring plan v${result.plan?.version} uploaded`,
        // Required by the notification type. Kept to the fact of what landed
        // rather than an explanation of how versioning works.
        description: result.plan?.original_filename ?? "",
      });
    },
    onError: (error: MedicalReviewApiError) =>
      openErrorNotification({
        title: "Could not upload the plan",
        description: error.message,
      }),
  });

  if (planQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spin />
      </div>
    );
  }
  if (planQuery.isError || !planQuery.data) {
    return (
      <Empty
        description="The monitoring plan is not available for this trial."
        className="my-16"
      />
    );
  }

  const plan = planQuery.data.plan;

  return (
    <div className="max-w-5xl">
      {contextHolder}
      {downloadContextHolder}
      <MonitoringPlanView
        plan={plan}
        canEdit={planQuery.data.can_edit}
        versionCount={planQuery.data.version_count}
        uploading={uploadMutation.isPending}
        downloading={downloading}
        onUpload={(draft) => uploadMutation.mutate(draft)}
        onDownload={() =>
          plan &&
          download({
            version: plan.version,
            filename: plan.original_filename,
          })
        }
        onOpenHistory={() => setHistoryOpen(true)}
      />
      <MonitoringPlanHistory
        trialId={trialId}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />
    </div>
  );
}
